import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

const ACCENT = '#a855f7';

const ALL_DRIVERS = [
  // Grille actuelle
  'Max Verstappen', 'Lewis Hamilton', 'Charles Leclerc', 'Lando Norris',
  'Fernando Alonso', 'Carlos Sainz', 'George Russell', 'Oscar Piastri',
  'Sergio Perez', 'Daniel Ricciardo', 'Sebastian Vettel', 'Valtteri Bottas',
  'Pierre Gasly', 'Yuki Tsunoda', 'Nico Hülkenberg', 'Alexander Albon',
  'Esteban Ocon', 'Lance Stroll', 'Kevin Magnussen', 'Zhou Guanyu',
  // Légendes
  'Michael Schumacher', 'Ayrton Senna', 'Alain Prost', 'Niki Lauda',
  'Nigel Mansell', 'Nelson Piquet', 'Damon Hill', 'Jackie Stewart',
  // Années 2000–2010
  'Mika Häkkinen', 'Kimi Räikkönen', 'Jenson Button', 'Nico Rosberg',
  'Mark Webber', 'Felipe Massa', 'David Coulthard', 'Rubens Barrichello',
  'Robert Kubica', 'Romain Grosjean', 'Pastor Maldonado',
  // Génération 2015–2022
  'Daniil Kvyat', 'Antonio Giovinazzi', 'Nicholas Latifi',
  // Pilotes actuels
  'Andrea Kimi Antonelli', 'Oliver Bearman', 'Liam Lawson',
  'Isack Hadjar', 'Franco Colapinto', 'Jack Doohan',
];

const ScoreBar = ({ score }) => {
  const color = score >= 70 ? '#22c55e' : score >= 40 ? '#f59e0b' : '#ef4444';
  return (
    <div className="w-full h-2 bg-zinc-800 rounded-full overflow-hidden">
      <div className="h-full rounded-full transition-all duration-500"
        style={{ width: `${Math.max(0, score)}%`, backgroundColor: color }} />
    </div>
  );
};

const initRound = () => ({
  hints: [], totalHints: 6, driverId: null,
  revealedCount: 1, score: 100, wrongCount: 0,
  selected: '', search: '', showDropdown: false, wrongFlash: false,
});

const WhoAmI = () => {
  const { user, refreshUser } = useAuth();
  const [phase, setPhase] = useState('loading');
  const [round, setRound] = useState(initRound());
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [sessionXp, setSessionXp] = useState(0);
  const [roundCount, setRoundCount] = useState(0);
  const seenIds = useRef([]);
  const hasInitialized = useRef(false);

  const loadDriver = useCallback(async () => {
    if (!user) { setPhase('error'); return; }
    setPhase('loading');
    try {
      const exclude = seenIds.current.join(',');
      const r = await api.get(`/game/guess/today${exclude ? `?exclude=${exclude}` : ''}`);
      seenIds.current = [...seenIds.current, r.data.driverId];
      setRound({ ...initRound(), hints: r.data.hints, totalHints: r.data.totalHints, driverId: r.data.driverId });
      setResult(null);
      setPhase('playing');
    } catch {
      setPhase('error');
    }
  }, [user]);

  useEffect(() => {
    if (user && !hasInitialized.current) {
      hasInitialized.current = true;
      loadDriver();
    }
  }, [user, loadDriver]);

  const set = (key, val) => setRound(p => ({ ...p, [key]: typeof val === 'function' ? val(p[key]) : val }));

  const nextHint = () => {
    if (round.revealedCount >= round.totalHints) return;
    set('revealedCount', v => v + 1);
    set('score', v => Math.max(0, v - 10));
  };

  const filteredDrivers = ALL_DRIVERS.filter(d =>
    d.toLowerCase().includes(round.search.toLowerCase()) && d !== round.selected
  );

  const selectDriver = (name) => setRound(p => ({ ...p, selected: name, search: name, showDropdown: false }));

  const handleGuess = async () => {
    if (!round.selected || submitting) return;
    setSubmitting(true);
    try {
      const r = await api.post('/game/guess/submit', {
        driverId: round.driverId,
        answer: round.selected,
        nextClicks: round.revealedCount - 1,
        wrongGuesses: round.wrongCount,
      });
      setSessionXp(p => p + r.data.xpEarned);
      setRoundCount(p => p + 1);
      setResult(r.data);
      setPhase('done');
      if (r.data.xpEarned > 0) refreshUser();
    } catch (err) {
      const data = err.response?.data;
      if (data?.wrong) {
        setRound(p => ({
          ...p,
          wrongCount: p.wrongCount + 1,
          score: Math.max(0, p.score - 50),
          wrongFlash: true,
          selected: '', search: '',
        }));
        setTimeout(() => set('wrongFlash', false), 1200);
      } else {
        setPhase('error');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleGiveUp = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const r = await api.post('/game/guess/submit', {
        driverId: round.driverId,
        answer: '__giveup__',
        nextClicks: round.revealedCount - 1,
        wrongGuesses: round.wrongCount,
        giveUp: true,
      });
      setRoundCount(p => p + 1);
      setResult(r.data);
      setPhase('done');
      refreshUser();
    } catch {
      setPhase('error');
    } finally {
      setSubmitting(false);
    }
  };

  // ── LOADING ────────────────────────────────────────────────────────────────
  if (phase === 'loading') return (
    <div className="min-h-screen flex items-center justify-center">
      <p className="text-zinc-400">Chargement du pilote...</p>
    </div>
  );

  if (phase === 'error') return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4">
      <p className="text-zinc-400">Erreur lors du chargement.</p>
      <Link to="/box-box" className="btn-secondary">← Box Box Hub</Link>
    </div>
  );

  // ── RESULTS ────────────────────────────────────────────────────────────────
  if (phase === 'done' && result) {
    return (
      <div className="max-w-md mx-auto px-4 py-10 text-center">
        <p className="text-6xl mb-4">{result.found ? (result.score >= 80 ? '🏆' : result.score >= 50 ? '🔥' : '👍') : '💀'}</p>
        <h1 className="text-3xl font-black text-white mb-1">
          {result.found ? `${result.score}/100 pts` : '0/100 pts'}
        </h1>
        <p className="text-zinc-400 mb-3">
          {result.found ? 'Bravo, tu as trouvé !' : `C'était…`}
        </p>
        <p className="text-xl font-black text-white mb-4">🕵️ {result.correct}</p>

        <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl mb-2"
          style={{ background: ACCENT + '18', border: `1px solid ${ACCENT}44` }}>
          <span className="font-black text-xl" style={{ color: ACCENT }}>+{result.xpEarned} XP</span>
        </div>
        {roundCount > 1 && (
          <p className="text-zinc-500 text-xs mb-2">Session : <span className="text-white font-bold">+{sessionXp} XP</span> en {roundCount} pilotes</p>
        )}
        {result.levelInfo?.leveledUp && (
          <p className="text-yellow-400 font-black mt-1 mb-3">🎉 Niveau {result.levelInfo.level} atteint !</p>
        )}

        <div className="flex flex-col gap-3 mt-6">
          <button
            onClick={loadDriver}
            className="w-full py-3 rounded-xl font-black text-white text-sm"
            style={{ background: ACCENT }}>
            🕵️ Pilote suivant
          </button>
          <Link to="/box-box" className="btn-secondary text-center">← Box Box Hub</Link>
        </div>
      </div>
    );
  }

  // ── PLAYING ────────────────────────────────────────────────────────────────
  const canNext = round.revealedCount < round.totalHints;

  return (
    <div className="max-w-lg mx-auto px-4 py-6">
      {/* Header */}
      <div className="mb-6">
        <p className="text-[10px] font-black uppercase tracking-widest mb-0.5" style={{ color: ACCENT }}>Qui Suis-Je ?</p>
        <h1 className="text-2xl font-black text-white leading-tight">Trouve le pilote</h1>
        <p className="text-zinc-500 text-xs mt-0.5">
          Indice {round.revealedCount}/{round.totalHints}
          {roundCount > 0 && <span> · Session : <span className="text-white">+{sessionXp} XP</span></span>}
        </p>
      </div>

      {/* Score */}
      <div className={`rounded-2xl border p-4 mb-5 transition-all duration-300 ${round.wrongFlash ? 'border-red-500/60 bg-red-500/10' : 'border-zinc-800 bg-zinc-900/50'}`}>
        <div className="flex items-center justify-between mb-2">
          <span className="text-zinc-400 text-xs font-bold uppercase tracking-wider">Score en jeu</span>
          <span className="font-black text-lg" style={{ color: round.score >= 70 ? '#22c55e' : round.score >= 40 ? '#f59e0b' : '#ef4444' }}>
            {round.score} pts → {round.score * 10} XP
          </span>
        </div>
        <ScoreBar score={round.score} />
        {round.wrongFlash && <p className="text-red-400 text-xs font-bold mt-2 text-center">❌ Mauvaise réponse — 50 pts</p>}
        <div className="flex gap-4 mt-3 text-zinc-600 text-[10px]">
          <span>Indice suivant : <span className="text-zinc-400">−10 pts</span></span>
          <span>Mauvaise réponse : <span className="text-zinc-400">−50 pts</span></span>
        </div>
      </div>

      {/* Hints */}
      <div className="rounded-2xl border border-zinc-800 overflow-hidden mb-5">
        <div className="px-4 py-2.5 border-b border-zinc-800 flex items-center justify-between">
          <span className="text-white font-black text-sm uppercase tracking-wider">Indices</span>
          <span className="text-zinc-600 text-xs">{round.revealedCount}/{round.totalHints} révélés</span>
        </div>
        <div className="divide-y divide-zinc-800/60">
          {round.hints.slice(0, round.revealedCount).map((hint, i) => (
            <div key={i} className="flex items-start gap-3 px-4 py-3">
              <span className="text-xs font-black mt-0.5 shrink-0" style={{ color: ACCENT }}>#{i + 1}</span>
              <p className="text-zinc-200 text-sm leading-relaxed">{hint}</p>
            </div>
          ))}
          {canNext && (
            <div className="flex items-center gap-3 px-4 py-3 opacity-30">
              <span className="text-xs font-black text-zinc-600">#{round.revealedCount + 1}</span>
              <p className="text-zinc-600 text-sm italic">Indice masqué — clique "Indice suivant"</p>
            </div>
          )}
        </div>
      </div>

      {/* Guess input */}
      <div className="mb-4 relative">
        <input
          type="text"
          value={round.search}
          onChange={e => setRound(p => ({ ...p, search: e.target.value, selected: '', showDropdown: true }))}
          onFocus={() => set('showDropdown', true)}
          onBlur={() => setTimeout(() => set('showDropdown', false), 150)}
          placeholder="Cherche un pilote…"
          className="w-full bg-zinc-900 border border-zinc-700 rounded-xl px-4 py-3 text-white text-sm outline-none focus:border-purple-500 transition-colors"
        />
        {round.showDropdown && round.search.length > 0 && filteredDrivers.length > 0 && (
          <div className="absolute z-10 w-full mt-1 bg-zinc-900 border border-zinc-700 rounded-xl overflow-hidden shadow-2xl">
            {filteredDrivers.slice(0, 6).map(name => (
              <button key={name} onMouseDown={() => selectDriver(name)}
                className="w-full text-left px-4 py-2.5 text-sm text-zinc-200 hover:bg-zinc-800 hover:text-white transition-colors">
                {name}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex gap-3 mb-3">
        <button
          onClick={handleGuess}
          disabled={!round.selected || submitting}
          className="flex-1 py-3 rounded-xl font-black text-sm text-white transition-all disabled:opacity-40"
          style={{ background: round.selected ? ACCENT : '#52525b' }}>
          {submitting ? '…' : '✓ Valider ma réponse'}
        </button>
        <button
          onClick={nextHint}
          disabled={!canNext || submitting}
          className="px-4 py-3 rounded-xl font-black text-sm border border-zinc-700 text-zinc-300 hover:bg-zinc-800 transition-all disabled:opacity-40">
          Indice −10pts
        </button>
      </div>

      <button
        onClick={handleGiveUp}
        disabled={submitting}
        className="w-full py-2 text-zinc-600 hover:text-zinc-400 text-xs transition-colors">
        Abandonner (0 XP)
      </button>
    </div>
  );
};

export default WhoAmI;
